import React, { memo, useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  leftEggVariants,
  rightEggVariants,
  winDotVariants,
} from "../animations/variants";
import { CrackOverlay } from "./CrackOverlay";
import { UI_STRINGS } from "../strings/uiStrings";
import { GAME_CONFIG } from "../config/gameConfig";
import { getEggAnimVariant } from "../utils/gameLogic";
import type { Player, PlayerRole, EggSide, GamePhase } from "../types";
import egg1 from "../assets/images/egg-1.png";
import egg2 from "../assets/images/egg-2.png";
import egg1broken from "../assets/images/egg-1-broken.png";
import egg2broken from "../assets/images/egg-2-broken.png";

interface EggDisplayProps {
  player: Player;
  role: PlayerRole;
  side: EggSide;
  phase: GamePhase;
  isWinner: boolean;
  isLoser: boolean;
  roundHistory: string[];
  sideLabel: string;
  panelZIndex?: number;
}

function EggDisplayComponent({
  player,
  role,
  side,
  phase,
  isWinner,
  isLoser,
  roundHistory,
  sideLabel,
  panelZIndex,
}: EggDisplayProps): React.ReactElement {
  const isLeft = side === "left";
  const roleText =
    role === "attacker" ? UI_STRINGS.roles.attacker : UI_STRINGS.roles.defender;

  const playerColor =
    side === "right" ? GAME_CONFIG.colors.you : GAME_CONFIG.colors.enemy;

  const variants = isLeft ? leftEggVariants : rightEggVariants;
  const variantName = getEggAnimVariant(phase, isWinner, isLoser, role);

  const [clashStage, setClashStage] = useState<0 | 1 | 2>(0);
  const clashTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    clashTimersRef.current.forEach(clearTimeout);
    clashTimersRef.current = [];

    if (phase !== "fighting") {
      setClashStage(0);
      return;
    }

    const { clash1Ms, clash2Ms } = GAME_CONFIG.animation.fightClashes;

    const t1 = setTimeout(() => setClashStage(1), clash1Ms + 80);
    const t2 = setTimeout(() => setClashStage(2), clash2Ms + 80);

    clashTimersRef.current = [t1, t2];

    return () => {
      clashTimersRef.current.forEach(clearTimeout);
      clashTimersRef.current = [];
    };
  }, [phase]);

  const showBroken =
    isLoser &&
    (phase === "impact" || phase === "round-result" || phase === "game-over");

  const eggSrc = showBroken
    ? side === "left"
      ? egg1broken
      : egg2broken
    : side === "left"
      ? egg1
      : egg2;

  const crackStage: 0 | 1 | 2 | 3 = showBroken
    ? 3
    : phase === "fighting"
      ? clashStage
      : 0;

  return (
    <div
      className="relative flex flex-1 flex-col min-h-[360px]"
      style={{ zIndex: panelZIndex }}
    >
      {/* WATERMARK */}
      <div
        className={`${isLoser && "!opacity-10"} absolute top-10 opacity-80 inset-0 flex flex-col justify-center overflow-hidden pointer-events-none select-none ${
          isLeft ? "items-start pl-2" : "items-end pr-2"
        }`}
        style={{ zIndex: 0 }}
      >
        {Array.from(
          { length: GAME_CONFIG.animation.watermarkRepeatCount },
          (_, i) => (
            <span
              key={i}
              className={`
        italic uppercase whitespace-nowrap
        text-[50px] leading-[1.05]
        tracking-[-0.03em] gilroy-font !font-[1000]
        ${i === 0 ? "text-white" : ""}
      `}
              style={
                i > 0
                  ? {
                      WebkitTextStroke:
                        "1px rgba(255, 255, 255, " + (1 - i * 0.05) + ")",
                      WebkitTextFillColor: "transparent",
                      color: "transparent",

                      opacity: 1 - i * 0.28,
                    }
                  : undefined
              }
            >
              {roleText}
            </span>
          ),
        )}
      </div>

      {/* ROLE HEADER */}
      <div
        className={`relative flex flex-col z-[5] ${
          isLeft ? "items-start" : "items-end"
        }`}
      >
        <span
          className="
            there-brat-font
            text-4xl
            font-bold italic
            tracking-[0.08em]
            leading-none
            uppercase select-none
          "
          style={{
            color: playerColor,
            textShadow: `0 0 12px ${playerColor}90`,
          }}
        >
          {sideLabel}
        </span>
      </div>

      {/* EGG */}
      <div className="relative z-10 flex flex-1 items-center justify-center pb-4">
        <motion.div
          className="relative shrink-0"
          variants={variants}
          animate={variantName}
          style={{
            willChange: "transform",
            width:
              phase === "fighting"
                ? GAME_CONFIG.eggInFightSize
                : GAME_CONFIG.eggIdleSize,
            height:
              phase === "fighting"
                ? GAME_CONFIG.eggInFightSize
                : GAME_CONFIG.eggIdleSize,
          }}
        >
          <AnimatePresence mode="wait">
            <motion.img
              key={eggSrc}
              src={eggSrc}
              alt={UI_STRINGS.aria.eggImage(player.name)}
              initial={{ opacity: 0.85 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              draggable={false}
              className="block w-full h-full object-contain select-none"
            />
          </AnimatePresence>

          <CrackOverlay stage={crackStage} />
        </motion.div>
      </div>

      {/* WIN DOTS */}
      <div
        className={`absolute bottom-3 z-10 flex gap-2 ${
          isLeft ? "left-5" : "right-5"
        }`}
      >
        {Array.from({ length: GAME_CONFIG.maxRounds }, (_, i) => {
          const roundIndex = isLeft ? i : GAME_CONFIG.maxRounds - 1 - i;
          const winnerId = roundHistory[roundIndex];
          const isWin = winnerId === player.id;
          const isLoss = winnerId !== undefined && winnerId !== player.id;
          const dotColor = isWin
            ? GAME_CONFIG.colors.roundWin
            : isLoss
              ? GAME_CONFIG.colors.enemy
              : null;
          return (
            <motion.div
              key={roundIndex}
              variants={winDotVariants}
              initial="hidden"
              animate={isWin || isLoss ? "visible" : "hidden"}
              className="w-3 h-3 rounded-full"
              style={{
                background: dotColor ?? "rgba(255,255,255,0.12)",
                border: `2px solid ${dotColor ?? "rgba(255,255,255,0.2)"}`,
                boxShadow: dotColor ? `0 0 8px ${dotColor}` : "none",
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

export const EggDisplay = memo(EggDisplayComponent);
